import { listeningParts, trackGroups } from '../../constants';

/** A clip as the audio editor sees it; `breakAfter` is the divider below it. */
type Clip = { breakAfter?: boolean };

const withBreak = <T extends Clip>(clip: T, breakAfter: boolean): T => {
  const { breakAfter: _old, ...rest } = clip;
  return (breakAfter ? { ...rest, breakAfter: true } : rest) as T;
};

/** A divider after the last clip separates nothing: drop it, so a new upload cannot revive it. */
export function normalizeDividers<T extends Clip>(tracks: T[]): T[] {
  const last = tracks.length - 1;
  return tracks.map((clip, i) => (i === last && clip.breakAfter ? withBreak(clip, false) : clip));
}

/** Adds or removes the divider below one clip. */
export function setDivider<T extends Clip>(tracks: T[], index: number, on: boolean): T[] {
  return tracks.map((clip, i) => (i === index ? withBreak(clip, on) : clip));
}

/** The clips of each part, in order, with the dividers taken off (they are rebuilt from the parts). */
function partsOf<T extends Clip>(tracks: T[]): T[][] {
  const parts: T[][] = [];
  trackGroups(tracks).forEach((group, i) => {
    (parts[group] ??= []).push(withBreak(tracks[i], false));
  });
  return parts;
}

/** Joins parts back into one list, with a divider on the last clip of every part but the final one. */
function joinParts<T extends Clip>(parts: T[][]): T[] {
  const filled = parts.filter((part) => part.length > 0);
  return filled.flatMap((part, p) =>
    part.map((clip, i) => (p < filled.length - 1 && i === part.length - 1 ? withBreak(clip, true) : clip)),
  );
}

/**
 * Moves one clip into a part: `group` is the part's number and `position` the
 * slot inside it, both counted on the list as it is now (so a slot after the
 * clip's own place needs no adjusting). Only this clip changes part; a part
 * left with no clips disappears, and with it its divider. Returns the same
 * list when nothing would change.
 */
export function moveClipTo<T extends Clip>(tracks: T[], from: number, group: number, position: number): T[] {
  const groups = trackGroups(tracks);
  const sourceGroup = groups[from];
  if (sourceGroup === undefined || group < 0 || group > groups[groups.length - 1]) return tracks;
  const sourcePosition = groups.slice(0, from).filter((g) => g === sourceGroup).length;
  if (group === sourceGroup && (position === sourcePosition || position === sourcePosition + 1)) return tracks;

  const parts = partsOf(tracks);
  const [clip] = parts[sourceGroup].splice(sourcePosition, 1);
  const slot = group === sourceGroup && sourcePosition < position ? position - 1 : position;
  parts[group].splice(Math.min(Math.max(slot, 0), parts[group].length), 0, clip);
  return joinParts(parts);
}

/** Where a dragged clip is dropped: on a row (its upper or lower half), or on the line between two clips. */
export type DropTarget = { kind: 'clip'; index: number; after: boolean } | { kind: 'divider'; index: number };

/** The part and slot a drop target stands for, or null when the target is not there. */
function slotOf(tracks: Clip[], from: number, target: DropTarget): { group: number; position: number } | null {
  const groups = trackGroups(tracks);
  const at = target.index;
  if (at < 0 || at >= tracks.length) return null;
  const positionOf = (index: number) => groups.slice(0, index).filter((g) => g === groups[index]).length;
  if (target.kind === 'clip') return { group: groups[at], position: positionOf(at) + (target.after ? 1 : 0) };
  if (at === tracks.length - 1) return null;
  if (!tracks[at].breakAfter) return { group: groups[at], position: positionOf(at) + 1 }; // a plain gap: between the two clips
  // On a divider the clip always crosses it: coming from above it starts the part below, from below it ends the part above.
  return from <= at
    ? { group: groups[at + 1], position: 0 }
    : { group: groups[at], position: positionOf(at) + 1 };
}

/** Drops the clip at `from` on a target. Returns the same list when nothing would change. */
export function dropClip<T extends Clip>(tracks: T[], from: number, target: DropTarget): T[] {
  const slot = slotOf(tracks, from, target);
  return slot ? moveClipTo(tracks, from, slot.group, slot.position) : tracks;
}

/** Moves a clip one place. At the edge of its part it joins the neighbouring part; no other clip changes part. */
export function moveClip<T extends Clip>(tracks: T[], index: number, dir: -1 | 1): T[] {
  const groups = trackGroups(tracks);
  const group = groups[index];
  if (group === undefined) return tracks;
  const size = (g: number) => groups.filter((x) => x === g).length;
  const position = groups.slice(0, index).filter((g) => g === group).length;
  if (dir === 1) {
    if (position < size(group) - 1) return moveClipTo(tracks, index, group, position + 2);
    return group < groups[groups.length - 1] ? moveClipTo(tracks, index, group + 1, 0) : tracks;
  }
  if (position > 0) return moveClipTo(tracks, index, group, position - 1);
  return group > 0 ? moveClipTo(tracks, index, group - 1, size(group - 1)) : tracks;
}

/** Removes a clip; its divider (if any) moves up to the clip before it. */
export function removeClip<T extends Clip>(tracks: T[], index: number): T[] {
  const carried = Boolean(tracks[index]?.breakAfter);
  const next = tracks.filter((_, i) => i !== index);
  const before = index - 1;
  return normalizeDividers(next.map((clip, i) => (i === before && carried ? withBreak(clip, true) : clip)));
}

/** The question ranges the dividers make, e.g. ['1–14', '15–28', '29–40']; empty with fewer than two groups. */
export function splitPreview(tracks: Clip[], questionCount: number): string[] {
  const groups = trackGroups(tracks);
  const groupCount = groups.length ? groups[groups.length - 1] + 1 : 0;
  if (groupCount < 2 || questionCount < 1) return [];
  const numbers = Array.from({ length: questionCount }, (_, i) => i + 1);
  return listeningParts(numbers, tracks.length, undefined, groupCount).map((part) => `${part[0]}–${part[part.length - 1]}`);
}
